import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "description", "additionalContext",
    "button", "spinner", "buttonText",
    "dataSourcesBox", "sourcesList",
    "confidenceBox", "confidenceText",
    "errorBox", "errorText",
    "fileInput", "fileList", "dropZone"
  ]

  connect() {
    this.selectedFiles = []

    // Wire drag events directly — data-action is unreliable for DnD because
    // the browser requires preventDefault() on dragover before it decides
    // whether to allow the drop.
    const dz = this.dropZoneTarget
    this._onDragOver  = e => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; dz.dataset.dragging = "true" }
    this._onDragLeave = e => { if (!e.relatedTarget || !dz.contains(e.relatedTarget)) delete dz.dataset.dragging }
    this._onDrop      = e => { e.preventDefault(); delete dz.dataset.dragging; this.addFiles(Array.from(e.dataTransfer.files)) }
    dz.addEventListener("dragover",  this._onDragOver)
    dz.addEventListener("dragleave", this._onDragLeave)
    dz.addEventListener("drop",      this._onDrop)
  }

  disconnect() {
    const dz = this.dropZoneTarget
    dz.removeEventListener("dragover",  this._onDragOver)
    dz.removeEventListener("dragleave", this._onDragLeave)
    dz.removeEventListener("drop",      this._onDrop)
  }

  selectFile() {
    this.fileInputTarget.click()
  }

  fileChanged() {
    this.addFiles(Array.from(this.fileInputTarget.files))
    this.fileInputTarget.value = ""
  }

  addFiles(newFiles) {
    newFiles.forEach(file => {
      const isDupe = this.selectedFiles.some(f => f.name === file.name && f.size === file.size)
      if (!isDupe) this.selectedFiles.push(file)
    })
    this.renderFileList()
  }

  removeFile(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.selectedFiles.splice(index, 1)
    this.renderFileList()
  }

  renderFileList() {
    if (this.selectedFiles.length === 0) {
      this.fileListTarget.innerHTML = ""
      return
    }
    this.fileListTarget.innerHTML = this.selectedFiles.map((file, i) => `
      <span class="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-white/10 pl-2.5 pr-1 py-0.5 text-xs text-gray-600 dark:text-gray-300">
        ${this.escapeHtml(file.name)}
        <button type="button"
                data-action="click->auto-research#removeFile"
                data-index="${i}"
                class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 px-0.5 leading-none">&times;</button>
      </span>
    `).join("")
  }

  readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        // data URL is "data:<type>;base64,<data>" — strip the prefix
        const base64 = e.target.result.split(",")[1]
        resolve(base64)
      }
      reader.onerror = () => reject(new Error(reader.error?.message || "File could not be read"))
      reader.readAsDataURL(file)
    })
  }

  async research(event) {
    event.preventDefault()

    const description = this.descriptionTarget.value.trim()
    if (!description) {
      this.descriptionTarget.focus()
      return
    }

    // Validate all files before starting
    const oversized = this.selectedFiles.find(f => f.size > 30 * 1024 * 1024)
    if (oversized) {
      alert(`"${oversized.name}" is too large. Please keep each file under 30 MB.`)
      return
    }

    this.clearError()
    this.setLoading(true)

    try {
      const filePayloads = await Promise.all(
        this.selectedFiles.map(async (file) => ({
          file_data: await this.readFileAsBase64(file),
          file_type: file.type
        }))
      )

      const payload = {
        audience_description: description,
        additional_context: this.hasAdditionalContextTarget ? this.additionalContextTarget.value : "",
        files: filePayloads
      }

      const response = await fetch("/audience_briefs/research", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err.error || `Request failed (${response.status})`)
      }

      // The request now runs in a background job; poll for the result so we
      // never hold an HTTP connection open through a multi-minute Claude call.
      const { id } = await response.json()
      const data = await this.pollResult(id)
      this.populateForm(data)
    } catch (e) {
      console.error("Research error:", e)
      this.showError(e.message)
    } finally {
      this.setLoading(false)
    }
  }

  showError(message) {
    if (!this.hasErrorBoxTarget) {
      alert("Research failed: " + message)
      return
    }
    this.errorTextTarget.textContent = message || "Something went wrong."
    this.errorBoxTarget.classList.remove("hidden")
  }

  clearError() {
    if (this.hasErrorBoxTarget) this.errorBoxTarget.classList.add("hidden")
  }

  // Polls research_status until the job completes or errors. Caps at ~6 min.
  async pollResult(id) {
    const intervalMs = 2000
    const maxAttempts = 180
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(r => setTimeout(r, intervalMs))
      const res = await fetch(`/audience_briefs/research_status?id=${encodeURIComponent(id)}`, {
        headers: { "Accept": "application/json" }
      })
      if (!res.ok) throw new Error(`Status check failed (${res.status})`)
      const data = await res.json()
      if (data.status === "complete") return data
      if (data.status === "error") throw new Error(data.error || "Research failed")
      // pending / running → keep polling
    }
    throw new Error("Timed out waiting for research to finish")
  }

  setLoading(loading) {
    this.buttonTarget.disabled = loading
    this.spinnerTarget.classList.toggle("hidden", !loading)
    if (loading) {
      this.startStatusCycle()
    } else {
      this.stopStatusCycle()
      this.buttonTextTarget.textContent = "Re-Research"
    }
  }

  startStatusCycle() {
    let elapsed = 0

    const update = () => {
      const mins = Math.floor(elapsed / 60)
      const secs = String(elapsed % 60).padStart(2, "0")
      const timer = elapsed > 0 ? ` (${mins > 0 ? mins + ":" : ""}${secs})` : ""
      this.buttonTextTarget.textContent = `Research in progress…${timer}`
      elapsed++
    }

    update()
    this._statusInterval = setInterval(update, 1000)
  }

  stopStatusCycle() {
    if (this._statusInterval) {
      clearInterval(this._statusInterval)
      this._statusInterval = null
    }
  }

  populateForm(data) {
    // Name field
    const nameField = document.getElementById("audience_brief_name")
    if (nameField && data.suggested_name) nameField.value = data.suggested_name

    // Research context
    const contextField = document.getElementById("audience_brief_research_context")
    if (contextField && data.research_context) contextField.value = data.research_context

    // Segments
    if (Array.isArray(data.segments) && data.segments.length) {
      const list = document.getElementById("segments-list")
      if (list) {
        list.innerHTML = ""
        data.segments.forEach((seg, i) => {
          const div = document.createElement("div")
          div.innerHTML = this.segmentRowHTML(i, seg).trim()
          list.appendChild(div.firstChild)
        })
        // Update the global segmentIndex so "Add Segment" continues from the right index
        window.segmentIndex = data.segments.length
        if (typeof updateTotal === "function") updateTotal()
      }
    }

    // Populate quota textarea (visible field, keep pretty-printed)
    const quotasField = document.getElementById("suggested_quotas_field")
    if (quotasField) quotasField.value = JSON.stringify(data.suggested_quotas || {}, null, 2)

    // Populate screener rows
    const screenerList = document.getElementById("screeners-list")
    if (screenerList && Array.isArray(data.suggested_screeners)) {
      screenerList.innerHTML = ""
      const rows = data.suggested_screeners.length ? data.suggested_screeners : [{ type: "include", criterion: "" }]
      rows.forEach(s => {
        const row = document.createElement("div")
        row.className = "screener-row flex gap-2 items-center"
        row.innerHTML = this.screenerRowHTML(s)
        screenerList.appendChild(row)
      })
    }
    this.setHidden("data_sources", data.data_sources || [])
    document.getElementById("confidence_notes_field").value = data.confidence_notes || ""

    // Show data sources
    if (this.hasDataSourcesBoxTarget && Array.isArray(data.data_sources) && data.data_sources.length) {
      this.dataSourcesBoxTarget.classList.remove("hidden")
      this.sourcesListTarget.innerHTML = data.data_sources
        .map(s => `<li class="text-xs text-gray-600 dark:text-gray-400">${this.escapeHtml(s)}</li>`)
        .join("")
    }

    // Show confidence notes
    if (this.hasConfidenceBoxTarget && data.confidence_notes) {
      this.confidenceBoxTarget.classList.remove("hidden")
      this.confidenceTextTarget.textContent = data.confidence_notes
    }
  }

  setHidden(id, value) {
    const el = document.getElementById(id + "_field")
    if (el) el.value = JSON.stringify(value)
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  screenerRowHTML(s) {
    const criterion = this.escapeHtml(s.criterion || "")
    const isExclude = s.type === "exclude"
    return `
      <select name="screeners[][type]"
              class="rounded-md border border-gray-300 dark:border-transparent bg-white dark:bg-white/5 px-2 py-1.5 text-sm text-gray-900 dark:text-white dark:outline-1 dark:-outline-offset-1 dark:outline-white/10 focus:outline-none focus:ring-1 focus:ring-indigo-500">
        <option value="include"${isExclude ? "" : " selected"}>Include</option>
        <option value="exclude"${isExclude ? " selected" : ""}>Exclude</option>
      </select>
      <textarea name="screeners[][criterion]" rows="2" placeholder="e.g., owns a dog"
                class="flex-1 rounded-md border border-gray-300 dark:border-transparent bg-white dark:bg-white/5 px-3 py-1.5 text-sm text-gray-900 dark:text-white dark:outline-1 dark:-outline-offset-1 dark:outline-white/10 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">${criterion}</textarea>
      <button type="button" onclick="removeScreenerRow(this)"
              class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 px-1 text-xl leading-none">&times;</button>`
  }

  segmentRowHTML(i, seg) {
    const name = this.escapeHtml(seg.name || "")
    const pct  = seg.percentage != null ? seg.percentage : ""
    const desc = this.escapeHtml(seg.description || "")
    const constraints = seg.constraints || {}
    const hasConstraints = Object.keys(constraints).length > 0
    const constraintsRaw = hasConstraints ? JSON.stringify(constraints) : ""
    const count = hasConstraints ? Object.values(constraints).flat().length : 0

    let pills = ""
    for (const [attr, values] of Object.entries(constraints)) {
      const label = attr.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
      for (const v of Array.isArray(values) ? values : [values]) {
        pills += `<span class="inline-flex items-center rounded-full bg-indigo-50 dark:bg-indigo-950 px-2 py-0.5 text-xs text-indigo-700 dark:text-indigo-300">${this.escapeHtml(label)}: ${this.escapeHtml(v)}</span>`
      }
    }

    return `
      <div class="segment-row border border-gray-200 dark:border-white/10 rounded-lg p-4 space-y-3" data-index="${i}">
        <div class="flex items-start justify-between gap-2">
          <div class="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div class="sm:col-span-2">
              <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Segment Name</label>
              <input type="text" name="segments[${i}][name]" value="${name}"
                     class="block w-full rounded-md border border-gray-300 dark:border-transparent bg-white dark:bg-white/5 px-2 py-1.5 text-sm text-gray-900 dark:text-white dark:outline-1 dark:-outline-offset-1 dark:outline-white/10 focus:outline-none focus:ring-1 focus:ring-indigo-500">
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">% of Panel (0–1)</label>
              <input type="number" name="segments[${i}][percentage]" value="${pct}" min="0" max="1" step="0.01"
                     class="pct-input block w-full rounded-md border border-gray-300 dark:border-transparent bg-white dark:bg-white/5 px-2 py-1.5 text-sm text-center text-gray-900 dark:text-white dark:outline-1 dark:-outline-offset-1 dark:outline-white/10 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                     oninput="updateTotal()">
            </div>
          </div>
          <button type="button" onclick="removeSegment(this)"
                  class="mt-5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 px-1 text-xl leading-none shrink-0">&times;</button>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description</label>
          <textarea name="segments[${i}][description]" rows="4"
                    class="block w-full rounded-md border border-gray-300 dark:border-transparent bg-white dark:bg-white/5 px-2 py-1.5 text-sm text-gray-900 dark:text-white dark:outline-1 dark:-outline-offset-1 dark:outline-white/10 focus:outline-none focus:ring-1 focus:ring-indigo-500">${desc}</textarea>
        </div>
        <div>
          <details${hasConstraints ? " open" : ""}>
            <summary class="text-xs font-medium text-indigo-600 dark:text-indigo-400 cursor-pointer hover:text-indigo-500 dark:hover:text-indigo-300 select-none">
              Demographic Constraints${count ? " (" + count + ")" : ""}
            </summary>
            <div class="mt-2 space-y-2">
              ${pills ? '<div class="flex flex-wrap gap-1.5">' + pills + '</div>' : ""}
              <textarea name="segments[${i}][constraints]" rows="2"
                        placeholder='{"gender": ["Male"], "age_band": ["18-24", "25-34"]}'
                        class="constraints-json block w-full rounded-md border border-gray-300 dark:border-transparent bg-white dark:bg-white/5 px-2 py-1.5 font-mono text-xs text-gray-900 dark:text-white dark:outline-1 dark:-outline-offset-1 dark:outline-white/10 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">${this.escapeHtml(constraintsRaw)}</textarea>
              <p class="text-xs text-gray-400 dark:text-gray-500">JSON object. Keys must match quota attribute names (e.g. gender, age_band, income_bracket). Values are arrays of allowed labels.</p>
            </div>
          </details>
        </div>
      </div>`
  }
}
